import { useEffect, useState, useMemo } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  Loader2,
  Plus,
  ArrowLeft,
  FileText,
  Send,
  CheckCircle2,
  Download,
  Trash2,
  X,
  DollarSign,
  Users,
  ListChecks,
  Receipt,
  CircleDollarSign,
} from "lucide-react";
import { api, formatApiErrorDetail, API } from "@/lib/api";

const STATUS_TABS = [
  { id: "all", label: "All" },
  { id: "draft", label: "Drafts" },
  { id: "sent", label: "Sent" },
  { id: "paid", label: "Paid" },
];

const statusClasses = (s) => {
  const map = {
    draft: "bg-cream border border-sand text-ink-soft",
    sent: "bg-navy-light/15 text-navy border border-navy/20",
    paid: "bg-navy text-cream border border-navy",
    overdue: "bg-red-100 text-red-800 border border-red-200",
    cancelled: "bg-cream-dark text-ink-soft border border-sand",
  };
  return `inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium ${map[s] || map.draft}`;
};

// Australian FY (Jul→Jun). Returns the calendar year the FY starts.
function currentFyStart() {
  const d = new Date();
  return d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1;
}
function fyLabel(y) { return `${y}/${String(y + 1).slice(-2)}`; }
function isInFy(isoDate, fyStartYear) {
  if (!isoDate || typeof isoDate !== "string") return false;
  const s = `${fyStartYear}-07-01`;
  const e = `${fyStartYear + 1}-06-30`;
  return isoDate >= s && isoDate <= e;
}

export default function InvoicesManager() {
  const [invoices, setInvoices] = useState([]);
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showAllYears, setShowAllYears] = useState(false);
  const [viewMode, setViewMode] = useState("clients"); // 'clients' | 'invoices'
  const [balances, setBalances] = useState([]);
  const [balancesLoading, setBalancesLoading] = useState(true);
  const [clientEmailFilter, setClientEmailFilter] = useState(null); // when drilled into a client

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/invoices");
      setInvoices(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  const loadBalances = async () => {
    setBalancesLoading(true);
    try {
      const { data } = await api.get("/admin/finance/client-balances");
      setBalances(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBalancesLoading(false);
    }
  };
  useEffect(() => { load(); loadBalances(); }, []);

  const fyStart = useMemo(() => currentFyStart(), []);

  const scoped = useMemo(
    () => {
      // When drilled into a specific client, always show all their invoices regardless of FY.
      if (clientEmailFilter) return invoices;
      return showAllYears ? invoices : invoices.filter((i) => isInFy(i.paid_at || i.issue_date, fyStart));
    },
    [invoices, showAllYears, fyStart, clientEmailFilter]
  );

  const filtered = useMemo(
    () => {
      let out = tab === "all" ? scoped : scoped.filter((i) => i.status === tab);
      if (clientEmailFilter) out = out.filter((i) => (i.client_email || "").toLowerCase() === clientEmailFilter.toLowerCase());
      return out;
    },
    [scoped, tab, clientEmailFilter]
  );

  const totals = useMemo(() => {
    // Paid total is counted ONLY when paid_at falls inside the current FY.
    const paid = invoices
      .filter((i) => i.status === "paid" && isInFy(i.paid_at, fyStart))
      .reduce((n, i) => n + (i.total || 0), 0);
    const outstanding = invoices
      .filter((i) => ["sent", "overdue"].includes(i.status))
      .reduce((n, i) => n + (i.total || 0), 0);
    const drafts = invoices.filter((i) => i.status === "draft").length;
    return { paid, outstanding, drafts };
  }, [invoices, fyStart]);

  if (selectedId) {
    return <InvoiceDetail invoiceId={selectedId} onBack={() => { setSelectedId(null); load(); loadBalances(); }} />;
  }

  return (
    <div data-testid="invoices-manager">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-ink-soft mb-1">Invoicing & payments</div>
          <h2 className="font-serif text-3xl text-ink">Send invoices, track who owes what.</h2>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="invoice-new-button"
          className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-2.5 text-sm font-medium hover:bg-navy-deep transition-colors"
        >
          <Plus size={14} /> New invoice
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <SummaryTile label={`Paid · FY ${fyLabel(fyStart)}`} value={`$${totals.paid.toFixed(2)}`} accent />
        <SummaryTile label="Outstanding" value={`$${totals.outstanding.toFixed(2)}`} />
        <SummaryTile label="Drafts" value={totals.drafts} />
      </div>

      <div className="inline-flex rounded-full bg-cream border border-sand/80 p-1 mb-4" data-testid="invoice-view-toggle">
        <button
          onClick={() => { setViewMode("clients"); setClientEmailFilter(null); }}
          data-testid="invoice-view-clients"
          className={`inline-flex items-center gap-2 px-4 py-1.5 text-xs uppercase tracking-[0.18em] rounded-full transition-all ${viewMode === "clients" ? "bg-navy text-cream" : "text-ink-soft hover:text-ink"}`}
        >
          <Users size={12} /> By client
        </button>
        <button
          onClick={() => setViewMode("invoices")}
          data-testid="invoice-view-invoices"
          className={`inline-flex items-center gap-2 px-4 py-1.5 text-xs uppercase tracking-[0.18em] rounded-full transition-all ${viewMode === "invoices" ? "bg-navy text-cream" : "text-ink-soft hover:text-ink"}`}
        >
          <ListChecks size={12} /> All invoices
        </button>
      </div>

      {viewMode === "clients" && !clientEmailFilter ? (
        <ClientBalancesList
          rows={balances}
          loading={balancesLoading}
          onOpen={(row) => { setClientEmailFilter(row.client_email); setViewMode("invoices"); }}
        />
      ) : (
        <>
          {clientEmailFilter && (
            <ClientFinanceHeader
              email={clientEmailFilter}
              balance={balances.find((b) => b.client_email === clientEmailFilter)}
              onBack={() => { setClientEmailFilter(null); setViewMode("clients"); }}
              onChanged={() => { load(); loadBalances(); }}
              invoices={invoices.filter((i) => (i.client_email || "").toLowerCase() === clientEmailFilter.toLowerCase())}
            />
          )}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="text-xs text-ink-soft">
              {clientEmailFilter
                ? "Showing every invoice for this client (all years)"
                : `Showing ${showAllYears ? "all invoices (all years)" : `invoices for FY ${fyLabel(fyStart)} — Jul ${fyStart} to Jun ${fyStart + 1}`}`}
            </div>
            {!clientEmailFilter && (
              <button
                type="button"
                onClick={() => setShowAllYears((v) => !v)}
                data-testid="invoices-toggle-all-years"
                className="inline-flex items-center gap-1.5 rounded-full bg-cream border border-sand hover:border-navy/40 text-ink-soft hover:text-ink px-3 py-1 text-[11px]"
              >
                {showAllYears ? `Show FY ${fyLabel(fyStart)} only` : "Show all years"}
              </button>
            )}
          </div>

          <div className="flex gap-1 mb-6 border-b border-sand/60">
            {STATUS_TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`invoice-filter-${t.id}`}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
                  tab === t.id ? "border-navy text-navy" : "border-transparent text-ink-soft hover:text-ink"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="py-16 flex items-center justify-center text-ink-muted"><Loader2 size={20} className="animate-spin mr-2" /> Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-ink-soft italic font-serif text-lg">No invoices in this list yet.</div>
          ) : (
            <div className="space-y-2" data-testid="invoices-list">
              {filtered.map((inv) => (
                <button
                  key={inv.id}
                  data-testid={`invoice-row-${inv.id}`}
                  onClick={() => setSelectedId(inv.id)}
                  className="w-full text-left bg-cream-soft border border-sand/70 rounded-2xl p-5 hover:border-navy/60 transition-colors flex items-center justify-between gap-4 flex-wrap"
                >
                  <div className="flex items-center gap-4">
                    <FileText size={18} className="text-navy" />
                    <div>
                      <div className="font-mono text-sm text-ink">{inv.number}</div>
                      <div className="text-xs text-ink-soft">{inv.client_name} · {format(new Date(inv.issue_date), "d MMM yyyy")}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="font-serif text-xl text-ink">${inv.total.toFixed(2)}</div>
                    <span className={statusClasses(inv.status)}>{inv.status}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {showCreate && (
        <InvoiceEditor onClose={() => setShowCreate(false)} onSaved={(id) => { setShowCreate(false); load(); loadBalances(); setSelectedId(id); }} />
      )}
    </div>
  );
}

function ClientBalancesList({ rows, loading, onOpen }) {
  const totals = useMemo(() => {
    const owing = rows.reduce((n, r) => n + (r.balance_owing > 0 ? r.balance_owing : 0), 0);
    const credit = rows.reduce((n, r) => n + (r.balance_owing < 0 ? -r.balance_owing : 0), 0);
    return { owing, credit };
  }, [rows]);
  const clientsOwing = useMemo(() => rows.filter((r) => r.balance_owing > 0).length, [rows]);
  const clientsInCredit = useMemo(() => rows.filter((r) => r.balance_owing < 0).length, [rows]);

  if (loading) {
    return <div className="py-16 flex items-center justify-center text-ink-muted"><Loader2 size={20} className="animate-spin mr-2" /> Loading balances…</div>;
  }
  if (rows.length === 0) {
    return <div className="text-center py-16 text-ink-soft italic font-serif text-lg">No invoices yet — create one to see client balances here.</div>;
  }

  return (
    <div data-testid="client-balances">
      <div className="mb-4 flex items-center justify-between text-xs text-ink-soft flex-wrap gap-2">
        <div>
          {clientsOwing} owing · {clientsInCredit} in credit · {rows.length} total
        </div>
        <div className="flex items-center gap-5">
          <div><span className="text-ink-soft">Owed to you: </span><strong className="text-navy font-serif text-base">${totals.owing.toFixed(2)}</strong></div>
          {totals.credit > 0 && (
            <div><span className="text-ink-soft">Credit held: </span><strong className="text-emerald-700 font-serif text-base">${totals.credit.toFixed(2)}</strong></div>
          )}
        </div>
      </div>
      <div className="space-y-2">
        {rows.map((r) => {
          const owing = r.balance_owing > 0;
          const daysOld = r.oldest_unpaid_date
            ? Math.floor((Date.now() - new Date(r.oldest_unpaid_date).getTime()) / 86400000)
            : null;
          return (
            <button
              key={r.client_email}
              data-testid={`client-balance-row-${r.client_email}`}
              onClick={() => onOpen(r)}
              className={`w-full text-left rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap transition-colors border ${
                owing
                  ? "bg-cream-soft border-navy/25 hover:border-navy/70"
                  : "bg-cream-soft border-sand/70 hover:border-navy/40"
              }`}
            >
              <div className="min-w-0 flex-grow">
                <div className="font-serif text-xl text-ink flex items-center gap-2 flex-wrap">
                  {r.client_name || r.client_email}
                  {r.archived && <span className="text-[10px] uppercase tracking-[0.18em] text-ink-soft bg-cream border border-sand px-2 py-0.5 rounded-full">Archived</span>}
                </div>
                <div className="text-xs text-ink-soft truncate">{r.client_email} · {r.invoice_count} invoice{r.invoice_count === 1 ? "" : "s"}</div>
              </div>
              <div className="flex items-center gap-6 text-right">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-ink-soft">Paid</div>
                  <div className="text-sm text-ink-muted">${r.total_paid.toFixed(2)}</div>
                </div>
                <div>
                  <div className={`text-[10px] uppercase tracking-[0.2em] ${r.balance_owing < 0 ? "text-emerald-700" : "text-ink-soft"}`}>
                    {r.balance_owing < 0 ? "In credit" : "Balance owing"}
                  </div>
                  <div className={`font-serif text-2xl ${
                    r.balance_owing > 0 ? "text-navy" : (r.balance_owing < 0 ? "text-emerald-700" : "text-ink-soft")
                  }`}>
                    ${Math.abs(r.balance_owing).toFixed(2)}
                  </div>
                  {owing && daysOld !== null && (
                    <div className="text-[10px] text-ink-soft italic">oldest {daysOld} day{daysOld === 1 ? "" : "s"}</div>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SummaryTile({ label, value, accent }) {
  return (
    <div className={`rounded-2xl p-5 border ${accent ? "bg-navy text-cream border-navy" : "bg-cream-soft border-sand/70"}`}>
      <div className="text-[11px] uppercase tracking-[0.22em] opacity-80 mb-1">{label}</div>
      <div className="font-serif text-3xl">{value}</div>
    </div>
  );
}

function ClientFinanceHeader({ email, balance, invoices, onBack, onChanged }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRecord, setShowRecord] = useState(false);

  const loadPayments = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/payments", { params: { client_email: email } });
      setPayments(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadPayments(); }, [email]);

  const delPayment = async (id) => {
    if (!window.confirm("Delete this payment record? If it was auto-created by 'Mark as paid', the invoice will revert to Sent.")) return;
    try {
      await api.delete(`/admin/payments/${id}`);
      toast.success("Payment deleted");
      await loadPayments();
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const bal = balance?.balance_owing ?? 0;
  const name = balance?.client_name || email;
  const inCredit = bal < 0;

  return (
    <div className="mb-5" data-testid="client-finance-header">
      <button onClick={onBack} data-testid="invoice-clear-client-filter" className="inline-flex items-center gap-1.5 text-xs text-navy hover:underline mb-4">
        <ArrowLeft size={12} /> Back to client list
      </button>

      <div className="bg-cream-soft border border-sand/70 rounded-2xl p-6 mb-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-ink-soft mb-1">Client finance</div>
            <h3 className="font-serif text-2xl text-ink mb-0.5">{name}</h3>
            <div className="text-xs text-ink-soft">{email}</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowRecord(true)}
            data-testid="record-payment-button"
            className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-2.5 text-sm font-medium hover:bg-navy-deep transition-colors"
          >
            <CircleDollarSign size={14} /> Record payment
          </button>
          <button
            onClick={() => {
              const token = localStorage.getItem("ams_token");
              fetch(`${API}/admin/finance/statement.pdf?client_email=${encodeURIComponent(email)}`, {
                headers: { Authorization: `Bearer ${token}` },
              })
                .then((r) => r.blob())
                .then((blob) => {
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url; a.download = `Statement-${name}.pdf`; a.click();
                  URL.revokeObjectURL(url);
                })
                .catch(() => toast.error("Could not download statement"));
            }}
            data-testid="statement-download"
            className="inline-flex items-center gap-2 rounded-full bg-cream border border-sand px-4 py-2.5 text-sm hover:border-navy transition-colors"
          >
            <Download size={14} /> Statement PDF
          </button>
          <button
            onClick={async () => {
              const extra = window.prompt("Add a personal note to the email? (optional)") || "";
              try {
                const r = await api.post("/admin/finance/statement/send", { client_email: email, extra_message: extra });
                toast.success(r.data.sent ? "Statement emailed" : "Email skipped (no Resend key)");
              } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail));
              }
            }}
            data-testid="statement-email"
            className="inline-flex items-center gap-2 rounded-full bg-navy-light text-cream px-4 py-2.5 text-sm hover:bg-navy transition-colors"
          >
            <Send size={14} /> Email statement
          </button>
        </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-4 mt-5">
          <TinyTile label="Invoiced" value={`$${(balance?.total_invoiced || 0).toFixed(2)}`} />
          <TinyTile label="Paid" value={`$${(balance?.total_paid || 0).toFixed(2)}`} />
          <TinyTile
            label={inCredit ? "In credit" : "Balance owing"}
            value={`$${Math.abs(bal).toFixed(2)}`}
            accent={bal > 0 ? "navy" : (inCredit ? "emerald" : "neutral")}
            testid="client-balance-tile"
          />
        </div>
      </div>

      <div className="bg-cream-soft border border-sand/70 rounded-2xl p-5" data-testid="payments-history">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-[0.22em] text-ink-soft inline-flex items-center gap-2">
            <Receipt size={12} /> Payment history
          </div>
          <div className="text-xs text-ink-soft">{payments.length} payment{payments.length === 1 ? "" : "s"}</div>
        </div>
        {loading ? (
          <div className="py-6 flex items-center justify-center text-ink-muted text-sm"><Loader2 size={14} className="animate-spin mr-2" />Loading…</div>
        ) : payments.length === 0 ? (
          <div className="py-6 text-center text-xs text-ink-soft italic">No payments recorded yet. Click "Record payment" to log one.</div>
        ) : (
          <div className="divide-y divide-sand/40">
            {payments.map((p) => (
              <div key={p.id} className="py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid={`payment-row-${p.id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-ink">
                    ${p.amount.toFixed(2)} · <span className="text-ink-muted">{p.method}</span>
                    {p.invoice_number && <span className="text-ink-soft"> · against {p.invoice_number}</span>}
                  </div>
                  <div className="text-[11px] text-ink-soft">
                    {format(new Date(p.date), "d MMM yyyy")}
                    {p.notes && <span className="italic"> · {p.notes}</span>}
                  </div>
                </div>
                <button
                  onClick={() => delPayment(p.id)}
                  data-testid={`payment-delete-${p.id}`}
                  className="text-[11px] text-red-700 hover:text-red-900 inline-flex items-center gap-1"
                >
                  <Trash2 size={11} /> Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {showRecord && (
        <RecordPaymentModal
          clientEmail={email}
          clientName={name}
          invoices={invoices.filter((i) => i.status !== "cancelled")}
          suggestedAmount={bal > 0 ? bal : null}
          onClose={() => setShowRecord(false)}
          onSaved={async () => {
            setShowRecord(false);
            await loadPayments();
            onChanged && onChanged();
          }}
        />
      )}
    </div>
  );
}

function TinyTile({ label, value, accent = "neutral", testid }) {
  const styles = {
    neutral: "bg-cream border-sand text-ink",
    navy: "bg-navy text-cream border-navy",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
  };
  return (
    <div className={`rounded-xl border p-4 ${styles[accent] || styles.neutral}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] opacity-80 mb-1">{label}</div>
      <div className="font-serif text-2xl">{value}</div>
    </div>
  );
}

function RecordPaymentModal({ clientEmail, clientName, invoices, suggestedAmount, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    amount: suggestedAmount ? suggestedAmount.toFixed(2) : "",
    date: today,
    method: "bank-transfer",
    invoice_id: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) {
      toast.error("Enter an amount greater than $0.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/payments", {
        client_email: clientEmail,
        amount: amt,
        date: form.date,
        method: form.method,
        invoice_id: form.invoice_id || "",
        notes: form.notes,
      });
      toast.success("Payment recorded");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full bg-cream border border-sand/70 rounded-lg px-3 py-2 text-sm text-ink focus:outline-none focus:border-navy";
  const labelCls = "block text-[10px] uppercase tracking-[0.22em] text-ink-soft mb-1";

  return (
    <div className="fixed inset-0 bg-navy-deep/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        className="bg-cream rounded-3xl p-6 w-full max-w-lg shadow-2xl"
        data-testid="record-payment-modal"
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-ink-soft mb-1">Record payment</div>
            <h3 className="font-serif text-2xl text-ink">{clientName}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink"><X size={16} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-1">
            <label className={labelCls}>Amount (AUD)</label>
            <input className={inputCls} type="number" step="0.01" min="0.01" required autoFocus value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount" />
          </div>
          <div className="col-span-1">
            <label className={labelCls}>Date received</label>
            <input className={inputCls} type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="payment-date" />
          </div>
          <div className="col-span-1">
            <label className={labelCls}>Method</label>
            <select className={inputCls} value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} data-testid="payment-method">
              <option value="bank-transfer">Bank transfer</option>
              <option value="payid">PayID</option>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="ndis">NDIS plan</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="col-span-1">
            <label className={labelCls}>Against invoice (optional)</label>
            <select className={inputCls} value={form.invoice_id} onChange={(e) => setForm({ ...form, invoice_id: e.target.value })} data-testid="payment-invoice">
              <option value="">— general credit —</option>
              {invoices.map((i) => (
                <option key={i.id} value={i.id}>{i.number} · ${i.total.toFixed(2)} · {i.status}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Notes (optional)</label>
            <input className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. part-payment, overpaid by $20, NDIS claim #..." data-testid="payment-notes" />
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm text-ink-soft hover:text-ink">Cancel</button>
          <button type="submit" disabled={saving} data-testid="payment-save" className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-2.5 text-sm font-medium hover:bg-navy-deep disabled:opacity-60">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Save payment
          </button>
        </div>
      </form>
    </div>
  );
}

function InvoiceDetail({ invoiceId, onBack }) {
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [showEdit, setShowEdit] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/invoices/${invoiceId}`);
      setInv(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [invoiceId]);

  const send = async () => {
    const extra = window.prompt("Anything personal to add to the email? (optional)") || "";
    setBusy("send");
    try {
      const r = await api.post(`/admin/invoices/${invoiceId}/send`, { extra_message: extra });
      toast.success(r.data.sent ? "Invoice emailed" : "Marked as sent (email skipped — no Resend key)");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };
  const markPaid = async () => {
    if (!window.confirm("Mark this invoice as paid?")) return;
    setBusy("paid");
    try {
      await api.post(`/admin/invoices/${invoiceId}/mark-paid`, { payment_method: "bank-transfer" });
      toast.success("Marked as paid");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(""); }
  };
  const downloadPdf = () => {
    const token = localStorage.getItem("ams_token");
    fetch(`${API}/admin/invoices/${invoiceId}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `${inv.number}.pdf`; a.click();
        URL.revokeObjectURL(url);
      });
  };
  const del = async () => {
    if (!window.confirm("Delete this invoice? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/invoices/${invoiceId}`);
      toast.success("Invoice deleted");
      onBack();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (loading) return <div className="py-16 flex items-center justify-center text-ink-muted"><Loader2 size={20} className="animate-spin mr-2" />Loading…</div>;
  if (!inv) return null;

  return (
    <div data-testid="invoice-detail">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-navy mb-6">
        <ArrowLeft size={14} /> Back to invoices
      </button>

      <div className="bg-cream-soft border border-sand/70 rounded-3xl p-7 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="font-mono text-sm text-ink-soft mb-1">{inv.number}</div>
            <h2 className="font-serif text-3xl text-ink mb-1">{inv.client_name}</h2>
            <div className="text-sm text-ink-muted">{inv.client_email}</div>
            {inv.client_address && (
              <pre className="text-sm text-ink-muted whitespace-pre-wrap font-sans mt-1">{inv.client_address}</pre>
            )}
          </div>
          <div className="text-right">
            <span className={statusClasses(inv.status)}>{inv.status}</span>
            <div className="font-serif text-4xl text-navy mt-3">${inv.total.toFixed(2)}</div>
            <div className="text-xs text-ink-soft mt-1">
              Issued {format(new Date(inv.issue_date), "d MMM yyyy")}
              {inv.due_date && ` · Due ${format(new Date(inv.due_date), "d MMM yyyy")}`}
            </div>
          </div>
        </div>

        <div className="border-t border-sand/60 pt-5">
          <div className="grid grid-cols-12 text-[10px] uppercase tracking-[0.22em] text-ink-soft mb-3">
            <div className="col-span-6">Description</div>
            <div className="col-span-2 text-right">Qty</div>
            <div className="col-span-2 text-right">Unit</div>
            <div className="col-span-2 text-right">Amount</div>
          </div>
          {inv.line_items.map((li, i) => (
            <div key={i} className="grid grid-cols-12 py-2 text-sm border-b border-sand/40 last:border-0">
              <div className="col-span-6 text-ink">{li.description}</div>
              <div className="col-span-2 text-right text-ink-muted">{li.quantity}</div>
              <div className="col-span-2 text-right text-ink-muted">${li.unit_price.toFixed(2)}</div>
              <div className="col-span-2 text-right text-ink font-medium">${li.amount.toFixed(2)}</div>
            </div>
          ))}
          <div className="flex justify-end mt-4 text-sm text-ink-soft italic">Not registered for GST — no GST applies.</div>
        </div>

        {inv.payment_instructions && (
          <div className="mt-5 pt-5 border-t border-sand/60">
            <div className="text-[10px] uppercase tracking-[0.22em] text-ink-soft mb-2">Payment</div>
            <pre className="text-sm text-ink whitespace-pre-wrap font-sans">{inv.payment_instructions}</pre>
          </div>
        )}
        {inv.notes && (
          <div className="mt-5 pt-5 border-t border-sand/60">
            <div className="text-[10px] uppercase tracking-[0.22em] text-ink-soft mb-2">Notes</div>
            <pre className="text-sm text-ink-muted whitespace-pre-wrap font-sans italic">{inv.notes}</pre>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <button onClick={downloadPdf} data-testid="invoice-download" className="inline-flex items-center gap-2 rounded-full bg-cream border border-sand px-5 py-2.5 text-sm hover:border-navy transition-colors">
          <Download size={14} /> Download PDF
        </button>
        {inv.status !== "paid" && (
          <button onClick={send} disabled={busy === "send"} data-testid="invoice-send" className="inline-flex items-center gap-2 rounded-full bg-navy-light text-cream px-5 py-2.5 text-sm hover:bg-navy transition-colors disabled:opacity-60">
            {busy === "send" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {inv.sent_at ? "Resend" : "Send via email"}
          </button>
        )}
        {inv.status !== "paid" && (
          <button onClick={markPaid} disabled={busy === "paid"} data-testid="invoice-mark-paid" className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-2.5 text-sm hover:bg-navy-deep transition-colors disabled:opacity-60">
            {busy === "paid" ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Mark as paid
          </button>
        )}
        {inv.status !== "paid" && (
          <button onClick={() => setShowEdit(true)} className="inline-flex items-center gap-2 rounded-full bg-cream border border-sand px-5 py-2.5 text-sm hover:border-navy transition-colors">
            Edit
          </button>
        )}
        <button onClick={del} className="inline-flex items-center gap-1.5 text-xs text-red-700 hover:text-red-900 ml-auto">
          <Trash2 size={12} /> Delete
        </button>
      </div>

      {showEdit && (
        <InvoiceEditor existing={inv} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); load(); }} />
      )}
    </div>
  );
}

function InvoiceEditor({ existing, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const defaultDue = new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10);
  // Stable client-side id so React keys stay correct across line add/remove
  const _lid = () => Math.random().toString(36).slice(2, 10);
  const [form, setForm] = useState(
    existing
      ? {
          client_name: existing.client_name,
          client_email: existing.client_email,
          client_address: existing.client_address || "",
          issue_date: existing.issue_date,
          due_date: existing.due_date || "",
          service_date: existing.service_date || existing.issue_date,
          notes: existing.notes || "",
          payment_instructions: existing.payment_instructions || "",
          line_items: existing.line_items.map((li) => ({ _lid: _lid(), description: li.description, quantity: li.quantity, unit_price: li.unit_price })),
        }
      : {
          client_name: "",
          client_email: "",
          client_address: "",
          issue_date: today,
          due_date: defaultDue,
          service_date: today,
          notes: "",
          payment_instructions: "Bank transfer\nBSB: 037-012\nAccount: 459 897\nName: A Mindful Space Counselling\nReference: invoice number\n\nPayment due within 14 days (two weeks) of the session date. Late fees apply after 14 days per our published policy.",
          line_items: [{ _lid: _lid(), description: "Individual counselling session — 60 min", quantity: 1, unit_price: 100 }],
        }
  );
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  // Quick-pick presets: real services + common extras. Editor is still free-text.
  const PRESETS = [
    ...services.map((s) => ({
      key: `svc-${s.slug}`,
      label: `${s.name} — ${s.duration_min} min · $${s.price}`,
      description: `${s.name} — ${s.duration_min} min`,
      unit_price: s.price,
    })),
    { key: "half-price", label: "Half-price session — $50", description: "Half-price counselling session — 60 min", unit_price: 50 },
    { key: "late-cancel", label: "Late cancellation fee — $50", description: "Late cancellation fee (less than 24h notice)", unit_price: 50 },
    { key: "no-show", label: "No-show fee — $100", description: "Missed appointment fee", unit_price: 100 },
  ];

  const applyPreset = (i, key) => {
    if (!key) return;
    const p = PRESETS.find((x) => x.key === key);
    if (!p) return;
    const lis = [...form.line_items];
    lis[i] = { ...lis[i], description: p.description, unit_price: p.unit_price, quantity: lis[i].quantity || 1 };
    setForm({ ...form, line_items: lis });
  };

  const subtotal = form.line_items.reduce((n, li) => n + Number(li.quantity || 0) * Number(li.unit_price || 0), 0);

  const pickClient = (email) => {
    const c = clients.find((x) => x.email === email);
    if (!c) return;
    setForm({ ...form, client_email: c.email, client_name: c.name || "", client_address: c.address || "" });
  };
  const updateLine = (i, key, val) => {
    const lis = [...form.line_items];
    lis[i] = { ...lis[i], [key]: val };
    setForm({ ...form, line_items: lis });
  };
  const addLine = () => setForm({ ...form, line_items: [...form.line_items, { _lid: _lid(), description: "", quantity: 1, unit_price: 0 }] });
  const removeLine = (i) => setForm({ ...form, line_items: form.line_items.filter((_, x) => x !== i) });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        // Strip client-only _lid before sending to backend
        line_items: form.line_items.map((li) => ({ description: li.description, quantity: Number(li.quantity), unit_price: Number(li.unit_price) })),
      };
      let id;
      if (existing) {
        await api.patch(`/admin/invoices/${existing.id}`, payload);
        id = existing.id;
        toast.success("Invoice updated");
      } else {
        const r = await api.post("/admin/invoices", payload);
        id = r.data.id;
        toast.success(`Invoice ${r.data.number} created`);
      }
      onSaved(id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full bg-cream border border-sand/80 rounded-xl px-3 py-2.5 text-ink focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy text-sm";
  const labelCls = "text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-1.5 block";

  return (
    <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-50 flex items-start justify-center p-6 overflow-y-auto" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        data-testid="invoice-editor"
        className="bg-cream-soft border border-sand/70 rounded-3xl p-7 w-full max-w-3xl mt-8 mb-8 shadow-2xl"
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-ink-soft">{existing ? "Edit invoice" : "New invoice"}</div>
            <h3 className="font-serif text-2xl text-ink">{existing ? existing.number : "Create an invoice"}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink"><X size={20} /></button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className={labelCls}>Existing client (optional)</label>
            <select
              className={inputCls}
              value=""
              onChange={(e) => pickClient(e.target.value)}
              data-testid="invoice-client-picker"
            >
              <option value="">— pick a client to autofill —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.email}>{c.name || c.email}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Client email</label>
            <input className={inputCls} type="email" required value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} data-testid="invoice-client-email" />
          </div>
          <div>
            <label className={labelCls}>Client name</label>
            <input className={inputCls} required value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="invoice-client-name" />
          </div>
          <div>
            <label className={labelCls}>Issue date</label>
            <input className={inputCls} type="date" required value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} data-testid="invoice-issue-date" />
          </div>
          <div>
            <label className={labelCls}>Service date <span className="text-ink-soft normal-case tracking-normal">(session date, for NDIS)</span></label>
            <input className={inputCls} type="date" required value={form.service_date} onChange={(e) => setForm({ ...form, service_date: e.target.value })} data-testid="invoice-service-date" />
          </div>
          <div>
            <label className={labelCls}>Due date (optional)</label>
            <input className={inputCls} type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="invoice-due-date" />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Client address (optional)</label>
            <textarea className={`${inputCls} resize-none`} rows={2} value={form.client_address} onChange={(e) => setForm({ ...form, client_address: e.target.value })} />
          </div>
        </div>

        <div className="mb-2 mt-5 flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.22em] text-ink-soft">Line items</div>
          <button type="button" onClick={addLine} className="text-xs text-navy hover:underline">+ Add line</button>
        </div>
        <div className="space-y-2">
          {form.line_items.map((li, i) => (
            <div key={li._lid} className="grid grid-cols-12 gap-2 items-start">
              <div className="col-span-6 space-y-1.5">
                <select
                  className={`${inputCls} text-xs`}
                  value=""
                  onChange={(e) => applyPreset(i, e.target.value)}
                  data-testid={`line-preset-${i}`}
                >
                  <option value="">— quick pick a service / fee —</option>
                  {PRESETS.map((p) => (
                    <option key={p.key} value={p.key}>{p.label}</option>
                  ))}
                </select>
                <input className={inputCls} placeholder="Description" required value={li.description} onChange={(e) => updateLine(i, "description", e.target.value)} data-testid={`line-desc-${i}`} />
              </div>
              <input className={`${inputCls} col-span-2 text-right`} type="number" step="0.1" min="0.1" value={li.quantity} onChange={(e) => updateLine(i, "quantity", e.target.value)} />
              <input className={`${inputCls} col-span-2 text-right`} type="number" step="0.01" min="0" value={li.unit_price} onChange={(e) => updateLine(i, "unit_price", e.target.value)} />
              <div className="col-span-1 py-2.5 text-right text-sm text-ink-muted">${(Number(li.quantity || 0) * Number(li.unit_price || 0)).toFixed(2)}</div>
              <button type="button" onClick={() => removeLine(i)} disabled={form.line_items.length === 1} className="col-span-1 text-ink-soft hover:text-red-700 py-2.5 disabled:opacity-30"><X size={14} /></button>
            </div>
          ))}
        </div>

        <div className="flex justify-end items-center gap-3 mt-4 pt-4 border-t border-sand/60">
          <div className="text-xs uppercase tracking-[0.18em] text-ink-soft">Total</div>
          <div className="font-serif text-2xl text-navy">${subtotal.toFixed(2)}</div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          <div>
            <label className={labelCls}>Payment instructions</label>
            <textarea className={`${inputCls} resize-none font-sans`} rows={3} value={form.payment_instructions} onChange={(e) => setForm({ ...form, payment_instructions: e.target.value })} />
          </div>
          <div>
            <label className={labelCls}>Notes (optional)</label>
            <textarea className={`${inputCls} resize-none font-sans`} rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>

        <button
          type="submit"
          disabled={saving || form.line_items.length === 0}
          data-testid="invoice-save-button"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy text-cream px-6 py-3 text-sm font-medium hover:bg-navy-deep transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <DollarSign size={14} />} {existing ? "Save changes" : "Create invoice"}
        </button>
      </form>
    </div>
  );
}
